import { Injectable, inject } from '@angular/core';
import { BaseApiService, PaginatedResults } from '@bo/ng-api';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Card,
  CardBalance,
  CardBlockRequest,
  CardFundsHistory,
  CardFundsRequest,
  CardFundsResponse,
  CardLimits,
  CardResponse,
  CardSdkConfig,
  CardType,
  SubscriptionCardRequest,
} from '../../../_interfaces/cards.interface';
import { catchError, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CardsApiService extends BaseApiService {
  protected override http: HttpClient;

  override baseUrl: string = environment.apiRoot;
  URL_CARD_PERSONAL: string = 'card/personal';
  URL_BALANCE: string = 'balance'; // Relative to URL_CARD_PERSONAL
  URL_CARD_BLOCK: string = 'card/block';
  URL_CARD_UNBLOCK: string = 'card/unblock';
  URL_CARD_REPLACE: string = 'card/replace';
  URL_CARD_SUBSCRIPTION: string = 'card/subscription';
  URL_CARD_SUBSCRIPTION_REMOVE: string = 'card/subscription/remove';
  URL_CONFIG_CARD_LIMITS: string = 'config/card-limits';
  URL_CARD_FUNDS_LOAD: string = 'card/funds-load';
  URL_CARD_FUNDS_DEDUCT: string = 'card/funds-deduct';
  URL_CARD_FUNDS_HISTORY: string = 'card/funds-history';
  URL_MEA_SDK_CONFIG: string = 'mea-sdk-config'; // Relative to URL_CARD_PERSONAL

  constructor() {
    const http = inject(HttpClient);

    super(http);

    this.http = http;
  }

  /**
   * Get all personal cards - Paginated
   *
   * @param queryParams
   * @return Observable<PaginatedResults<Card[]>>
   */
  getCardPersonal(queryParams: HttpParams): Observable<PaginatedResults<Card[]>> {
    const url = this.getUrl(this.URL_CARD_PERSONAL, true);
    return this.http.get<Card[]>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<Card[]>(res);
      }),
    );
  }

  /**
   * Get personal cards with default status filter to hide removed cards
   * Adds status filter (0,1,10) if not already present in query params
   *
   * @param queryParams
   * @return Observable<PaginatedResults<Card[]>>
   */
  getCardPersonalHideRemoved(queryParams: HttpParams): Observable<PaginatedResults<Card[]>> {
    const url = this.getUrl(this.URL_CARD_PERSONAL, true);

    // Add default status filter if not already present
    // Status 0 = New, 1 = Active, 10 = Blocked (excludes 20 = Removed)
    if (!queryParams.has('status')) {
      queryParams = queryParams.set('status', '0,1,10');
    }

    return this.http.get<Card[]>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<Card[]>(res);
      }),
    );
  }

  /**
   * Get all personal cards - Not Paginated
   *
   * @return Observable<Card[]>
   */
  getAllPersonalCards(): Observable<Card[]> {
    const url = this.getUrl(this.URL_CARD_PERSONAL, true);
    return this.http.get<Card[]>(url);
  }

  /**
   * Get a personal card by ID
   *
   * @param cardId
   * @return Observable<Card>
   */
  getCardPersonalById(cardId: number) {
    const url = this.getUrl([this.URL_CARD_PERSONAL, cardId], true);
    return this.http.get<Card>(url);
  }

  /**
   * Get personal card balance
   *
   * @param cardId
   * @return Observable<CardBalance>
   */
  getPersonalCardBalance(cardId: number): Observable<CardBalance> {
    const url = this.getUrl([this.URL_CARD_PERSONAL, cardId, this.URL_BALANCE], true);
    return this.http.get<CardBalance>(url);
  }

  /**
   * Block cards Personal / Subscription
   *
   * @param data
   * @return Observable<CardResponse>
   */
  blockCard(data: CardBlockRequest): Observable<CardResponse> {
    return this.http.post<CardResponse>(this.getUrl(this.URL_CARD_BLOCK, true), data);
  }

  /**
   * Unblock cards Personal / Subscription
   *
   * @return Observable<CardResponse>
   * @param cardId
   */
  unblockCard(cardId: number): Observable<CardResponse> {
    return this.http.post<CardResponse>(this.getUrl(this.URL_CARD_UNBLOCK, true), { card: cardId });
  }

  /**
   * Replace cards Personal / Subscription
   *
   * @return Observable<CardResponse>
   * @param cardId
   */
  replaceCard(cardId: number): Observable<CardResponse> {
    return this.http.post<CardResponse>(this.getUrl(this.URL_CARD_REPLACE, true), { card: cardId });
  }

  /**
   * Create a new Subscription Card
   *
   * @return Observable<SubscriptionCardRequest>
   * @param data
   */
  createSubscriptionCard(data: SubscriptionCardRequest): Observable<SubscriptionCardRequest> {
    return this.http.post<SubscriptionCardRequest>(this.getUrl(this.URL_CARD_SUBSCRIPTION, true), data);
  }

  /**
   * Get Subscription cards paginated results
   *
   * @return Observable<Card[]>
   * @param queryParams
   */
  getSubscriptionCardsHideRemoved(queryParams: HttpParams): Observable<PaginatedResults<Card[]>> {
    // Check if queryParams already contains 'status' parameter
    // If not, add default status filter '0,1,10'
    const params = queryParams.has('status') ? queryParams : queryParams.append('status', '0,1,10');

    const url = this.getUrl(this.URL_CARD_SUBSCRIPTION, true);
    return this.http.get<Card[]>(url, { observe: 'response', params: params }).pipe(
      map((res) => {
        return new PaginatedResults<Card[]>(res);
      }),
      catchError(() => {
        return [];
      }),
    );
  }

  /**
   * Get Subscription cards paginated results
   *
   * @return Observable<Card[]>
   * @param queryParams
   */
  getSubscriptionCards(queryParams: HttpParams): Observable<PaginatedResults<Card[]>> {
    const url = this.getUrl(this.URL_CARD_SUBSCRIPTION, true);
    return this.http.get<Card[]>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<Card[]>(res);
      }),
      catchError(() => {
        return [];
      }),
    );
  }

  /**
   * Get all subscription cards - Not Paginated
   *
   * @return Observable<Card[]>
   */
  getAllSubscriptionCards(): Observable<Card[]> {
    const url = this.getUrl(this.URL_CARD_SUBSCRIPTION, true);
    return this.http.get<Card[]>(url);
  }

  /**
   * Get a Subscription card by Id
   *
   * @return Observable<Card>
   * @param cardId
   */
  getSubscriptionCardById(cardId: number): Observable<Card> {
    const url = this.getUrl([this.URL_CARD_SUBSCRIPTION, cardId], true);
    return this.http.get<Card>(url);
  }

  /**
   * Get Subscription card balance
   *
   * @return Observable<CardBalance>
   * @param cardId
   */
  getSubscriptionCardBalance(cardId: number): Observable<CardBalance> {
    const url = this.getUrl([this.URL_CARD_SUBSCRIPTION, cardId, this.URL_BALANCE], true);
    return this.http.get<CardBalance>(url);
  }

  /**
   * Remove Subscription card
   *
   * @return Observable<CardResponse>
   * @param cardId
   */
  removeSubscriptionCard(cardId: number): Observable<CardResponse> {
    return this.http.post<CardResponse>(this.getUrl(this.URL_CARD_SUBSCRIPTION_REMOVE, true), { card: cardId });
  }

  /**
   * Get card limits
   *
   * @return Observable<CardLimits>
   */
  getCardLimits(): Observable<CardLimits> {
    const url = this.getUrl([this.URL_CONFIG_CARD_LIMITS], true);
    return this.http.get<CardLimits>(url);
  }

  /**
   * Request load / top-up of funds on cards
   *
   * @return Observable<CardFundsResponse>
   * @param data
   */
  loadCardFunds(data: CardFundsRequest): Observable<CardFundsResponse> {
    return this.http.post<CardFundsResponse>(this.getUrl(this.URL_CARD_FUNDS_LOAD, true), data);
  }

  /**
   * Request deduction of funds on cards
   *
   * @return Observable<CardFundsResponse>
   * @param data
   */
  deductCardFunds(data: CardFundsRequest): Observable<CardFundsResponse> {
    return this.http.post<CardFundsResponse>(this.getUrl(this.URL_CARD_FUNDS_DEDUCT, true), data);
  }

  /**
   * Get card funds transaction history (Immediate top-ups) - Paginated
   *
   * @param queryParams
   * @return Observable<PaginatedResults<CardFundsHistory[]>>
   */
  getCardFundsTransactionHistory(queryParams: HttpParams): Observable<PaginatedResults<CardFundsHistory[]>> {
    const url = this.getUrl(this.URL_CARD_FUNDS_HISTORY, true);
    return this.http.get<CardFundsHistory[]>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<CardFundsHistory[]>(res);
      }),
    );
  }

  /**
   * Get Personal Virtual Card SDK Config for iframe
   *
   * @return Observable<CardSdkConfig>
   */
  getCardSdkConfig(id: number, cardType: CardType): Observable<CardSdkConfig> {
    const cardTypeUrl = cardType === CardType.Personal ? this.URL_CARD_PERSONAL : this.URL_CARD_SUBSCRIPTION;
    const url = this.getUrl([cardTypeUrl, id, this.URL_MEA_SDK_CONFIG], true);
    return this.http.get<CardSdkConfig>(url);
  }
}
